import { __decorate } from "tslib";
import { customElement, property } from 'lit/decorators.js';
import HelixElement from '../../shared/types/helix-element.js';
import { localized } from '@lit/localize';
import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { state } from 'lit/decorators.js';
import { IntervalTimer } from './interval-timer.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { notificationBarStyles } from './notification-bar.styles.js';
import { HlxNotification } from './notification.js';
import { createRef, ref } from 'lit-html/directives/ref.js';
import { query } from 'lit/decorators.js';
import { provide } from '@lit/context';
import { notificationBarContext, NotificationBarContext, notificationBarCurrentId, } from './notification-bar-context.js';
import { styleMap } from 'lit/directives/style-map.js';
let HlxNotificationBar = class HlxNotificationBar extends HelixElement {
    constructor() {
        super(...arguments);
        this.timeInterval = 10000;
        this.currentNotificationIndex = 0;
        this.context = new NotificationBarContext();
        this.currentNotificationId = null;
        this.expanded = false;
        this.intervalTimer = null;
        this.isFocused = false;
        this.isHovered = false;
        this.timerElementRef = createRef();
        this.notifications = [];
        this.notificationSlotRef = createRef();
        this.readNotificationIdsSet = new Set();
    }
    get timerPaused() {
        return this.isFocused || this.isHovered || this.expanded;
    }
    firstUpdated() {
        this.setupListenersForMultipleNotificationsMode();
    }
    async connectedCallback() {
        super.connectedCallback();
        this.setupInternalEventListeners();
    }
    async handleSlotChange() {
        this.updateNotifications();
        this.renderCurrentNotification();
        this.setupIntervalTimer();
        if (this.notifications.length > 1) {
            this.timerElementRef.value.classList.add('hlx-timer-animation');
        }
    }
    updateNotifications() {
        this.notifications = this.notificationSlotRef.value
            .assignedNodes()
            .filter((element) => element instanceof HlxNotification && !element.isClosed);
    }
    setupInternalEventListeners() {
        this.context.onToggle((expanded) => {
            this.expanded = expanded;
            if (this.notifications.length > 1 && this.intervalTimer) {
                this.updateIntervalTimer();
            }
        });
        this.context.onClose(() => {
            this.notifications[this.currentNotificationIndex].isClosed = true;
            const isLast = this.currentNotificationIndex === this.notifications.length - 1;
            if (isLast) {
                this.currentNotificationIndex = 0;
            }
            this.isFocused = false;
            this.isHovered = false;
            this.updateNotifications();
            this.renderCurrentNotification();
            this.intervalTimer.clear();
            if (this.notifications.length > 1) {
                this.intervalTimer.start();
                this.restartTimerAnimation();
                this.updateIntervalTimer();
            }
        });
    }
    disconnectedCallback() {
        this.intervalTimer?.clear();
        super.disconnectedCallback();
    }
    render() {
        return html `
            <div class="hlx-notification-bar">
                <div
                    data-testid="timer"
                    style=${styleMap({
            'animation-duration': `${this.timeInterval / 1000}s`,
        })}
                    class=${classMap({
            'hlx-timer': true,
            'hlx-timer-paused': this.timerPaused,
            'hlx-timer-hidden': this.notifications.length <= 1,
        })}
                    ${ref(this.timerElementRef)}></div>
                <div id="notification-container">
                    <slot
                        ${ref(this.notificationSlotRef)}
                        @slotchange=${this.handleSlotChange}></slot>
                </div>
            </div>
        `;
    }
    setupIntervalTimer() {
        if (this.intervalTimer) {
            this.intervalTimer.clear();
        }
        this.intervalTimer = new IntervalTimer(() => {
            if (this.currentNotificationIndex === this.notifications.length - 1) {
                this.currentNotificationIndex = 0;
            }
            else {
                this.currentNotificationIndex += 1;
            }
            this.renderCurrentNotification();
        }, this.timeInterval);
        this.intervalTimer.start();
    }
    setupListenersForMultipleNotificationsMode() {
        this.context.onFocus(() => {
            this.isFocused = true;
            this.updateIntervalTimer();
        });
        this.context.onBlur(() => {
            this.isFocused = false;
            this.updateIntervalTimer();
        });
        this.context.onMouseEnter(() => {
            this.isHovered = true;
            this.updateIntervalTimer();
        });
        this.context.onMouseLeave(() => {
            this.isHovered = false;
            this.updateIntervalTimer();
        });
    }
    updateIntervalTimer() {
        this.timerPaused ? this.intervalTimer.pause() : this.intervalTimer.resume();
    }
    restartTimerAnimation() {
        this.timerElementRef.value.classList.remove('hlx-timer-animation');
        // trigger reflow to restart css animation
        // see https://css-tricks.com/restart-css-animation/
        void this.timerElementRef.value.offsetWidth;
        this.timerElementRef.value.classList.add('hlx-timer-animation');
    }
    renderCurrentNotification() {
        if (!this.notifications.length) {
            return;
        }
        this.currentNotificationId =
            this.notifications[this.currentNotificationIndex].notificationId;
        this.updateScreenAnnouncer();
    }
    updateScreenAnnouncer() {
        this.notificationContainer.removeAttribute('aria-live');
        if (!this.readNotificationIdsSet.has(this.currentNotificationId)) {
            this.notificationContainer.setAttribute('aria-live', 'assertive');
            this.readNotificationIdsSet.add(this.currentNotificationId);
        }
    }
};
HlxNotificationBar.styles = [componentStyles, notificationBarStyles];
__decorate([
    property({ type: Number, attribute: 'time-interval' })
], HlxNotificationBar.prototype, "timeInterval", void 0);
__decorate([
    query('#notification-container')
], HlxNotificationBar.prototype, "notificationContainer", void 0);
__decorate([
    provide({ context: notificationBarContext })
], HlxNotificationBar.prototype, "context", void 0);
__decorate([
    provide({ context: notificationBarCurrentId }),
    state()
], HlxNotificationBar.prototype, "currentNotificationId", void 0);
__decorate([
    state()
], HlxNotificationBar.prototype, "expanded", void 0);
__decorate([
    state()
], HlxNotificationBar.prototype, "intervalTimer", void 0);
__decorate([
    state()
], HlxNotificationBar.prototype, "isFocused", void 0);
__decorate([
    state()
], HlxNotificationBar.prototype, "isHovered", void 0);
HlxNotificationBar = __decorate([
    customElement('hlx-notification-bar'),
    localized()
], HlxNotificationBar);
export { HlxNotificationBar };
//# sourceMappingURL=notification-bar.js.map